import clsx from 'clsx'

import { ExternalLink } from '@/generated/contentful'

import { isDevelopment } from '@/utils/isDevelopment'

import { Button, ButtonContentProps } from '@/components/Button'
import { Warning } from '@/components/Warning'

import { useEntry } from '../Article.hooks'
import { MarketoCTA } from './MarketoCTA'

type Props = {
	className?: string
	buttonColor?: ButtonContentProps['color']
}

export function ArticleSecondaryCTAs({ className, buttonColor }: Props) {
	const entry = useEntry()
	const isExternalLink = (entry: any): entry is ExternalLink => entry?.__typename === 'ExternalLink'

	if ('error' in entry) {
		if (isDevelopment()) {
			return <Warning className={className}>{entry.error}</Warning>
		}
		return null
	}

	const items = entry?.data?.secondaryCtasCollection?.items ?? []

	if (items.length === 0) {
		if (isDevelopment()) {
			return <Warning className={className}>Secondary CTAs are empty for this article</Warning>
		}

		return null
	}
	return (
		<div className={clsx(className, 'w-full bg-gray-1300 py-12')}>
			<div className="grid grid-flow-row md:grid-flow-col gap-4 min-h-[40px] max-w-[1156px] mx-auto">
				{items?.map((item) => {
					const cta = item?.ctaButtonCollection?.items?.[0]
					const formID = item?.ctaButtonCollection?.items[0]?.formId
					const link = {
						href: isExternalLink(cta?.link) ? cta?.link?.url?.toString() : undefined,
						target: item?.ctaButtonCollection?.items?.[0]?.openInNewTab ? '_blank' : '_self'
					}

					if (formID) {
						return (
							<MarketoCTA
								key={item?.title}
								formId={formID}
								displayText={item?.ctaButtonCollection?.items?.[0]?.displayText}
								title={item?.title}
								slug={entry?.data?.slug}
							/>
						)
					}

					return (
						<div key={item?.title} className="grid grid-flow-row gap-4 px-4">
							<h5 className="text-[20px] leading-[27px] text-black-100 font-medium">
								{item?.title}
							</h5>
							<Button
								className="w-fit"
								variant="subtle"
								color={buttonColor}
								size="large"
								showIcon={true}
								link={link}
							>
								<span className="uppercase">
									{item?.ctaButtonCollection?.items?.[0]?.displayText}
								</span>
							</Button>
						</div>
					)
				})}
			</div>
		</div>
	)
}
